import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Platform,
  Dimensions,
  Alert,
} from 'react-native';

import { ConfirmationDialog } from './ConfirmationDialog';
import {
  ChevronDown,
  LogOut,
  Sliders,
  FileText,
  User,
  MessageSquare,
} from './icons';
import { SyncStatus } from './SyncStatus';
import { SendFeedbackDialog } from './userFeedback/SendFeedbackDialog';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { getVersionDisplay } from '../utils/appVersion';

const isWeb = Platform.OS === 'web';

interface AccountDropdownProps {
  inline?: boolean; // When true, renders dropdown inline instead of using modal
  hideSettingsText?: boolean; // When true, hides the "Settings" text in the trigger
  noBorder?: boolean; // When true, removes border and background from trigger
  hideLogout?: boolean; // When true, hides the logout button
  navigate?: (path: string) => void; // Navigation function for Preferences menu
  disabled?: boolean; // When true, disables the dropdown trigger
}

export const AccountDropdown: React.FC<AccountDropdownProps> = ({
  inline = false,
  hideSettingsText = false,
  noBorder = false,
  hideLogout = false,
  navigate,
  disabled = false,
}) => {
  const { user, logout } = useAuth();
  const { isImpersonating, mode, session, endImpersonation } =
    useImpersonation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [sendFeedbackOpen, setSendFeedbackOpen] = useState(false);
  const hideSendFeedback = isImpersonating && mode === 'read_only';
  const buttonRef = useRef<TouchableOpacity>(null);

  // Get version info
  const versionDisplay = getVersionDisplay();

  // Determine if should be open by default (mobile web or native)
  const shouldOpenByDefault = () => {
    if (!isWeb) return true; // Always open on native

    // On web, check if mobile sized
    const { width } = Dimensions.get('window');
    return width <= 768; // Mobile breakpoint
  };

  const [isOpen, setIsOpen] = useState(shouldOpenByDefault);

  // Handle window resize on web to update open state
  useEffect(() => {
    if (isWeb) {
      const handleResize = () => {
        const newShouldOpen = shouldOpenByDefault();
        setIsOpen(newShouldOpen);
      };

      // Listen for window resize events
      const subscription = Dimensions.addEventListener('change', handleResize);

      return () => subscription?.remove();
    }
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleConfirmLogout = async () => {
    try {
      await endImpersonation();
    } catch {
      // Continue with logout even if end-impersonation fails
    }
    await logout();
    closeDropdown();
  };

  const handleLogout = () => {
    if (isImpersonating) {
      if (Platform.OS !== 'web') {
        // Native: use system Alert to avoid nested Modal issues
        Alert.alert(
          'Logout',
          `You are currently impersonating ${
            session?.target_user?.name || 'another user'
          }. Logging out will end the impersonation session and log out your account.`,
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Logout',
              style: 'destructive',
              onPress: handleConfirmLogout,
            },
          ],
        );
      } else {
        setShowLogoutConfirm(true);
      }
      return;
    }
    logout();
    closeDropdown();
  };

  const handleProfilePress = () => {
    if (navigate) {
      navigate('/profile');
    }
    closeDropdown();
  };

  const handlePreferencesPress = () => {
    if (navigate) {
      navigate('/preferences');
    }
    closeDropdown();
  };

  const handleSendFeedbackPress = () => {
    closeDropdown();
    setSendFeedbackOpen(true);
  };

  const handleTermsConditionsPress = () => {
    if (navigate) {
      navigate('/terms-conditions');
    }
    closeDropdown();
  };

  if (!user) {
    return null;
  }

  const initials =
    String(user.name || user.email || 'U')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join('') || 'U';

  const DropdownContent = (
    <View style={inline ? styles.dropdownContentInline : styles.dropdown}>
      <View style={inline ? styles.userInfoInline : styles.userInfo}>
        <Text style={styles.userName}>{user.name}</Text>
        <Text style={styles.userEmail}>{user.email}</Text>
      </View>

      {!inline && <View style={styles.divider} />}

      {navigate && (
        <TouchableOpacity
          testID="nav-profile"
          style={
            inline ? styles.preferencesButtonInline : styles.preferencesButton
          }
          onPress={handleProfilePress}
        >
          <User size={16} color="#666" />
          <Text style={styles.preferencesText}>Profile</Text>
        </TouchableOpacity>
      )}

      {navigate && (
        <TouchableOpacity
          testID="nav-preferences"
          style={
            inline ? styles.preferencesButtonInline : styles.preferencesButton
          }
          onPress={handlePreferencesPress}
        >
          <Sliders size={16} color="#666" />
          <Text style={styles.preferencesText}>Preferences</Text>
        </TouchableOpacity>
      )}

      {!hideSendFeedback && (
        <TouchableOpacity
          testID="nav-send-feedback"
          style={
            inline ? styles.preferencesButtonInline : styles.preferencesButton
          }
          onPress={handleSendFeedbackPress}
        >
          <MessageSquare size={16} color="#666" />
          <Text style={styles.preferencesText}>Send feedback</Text>
        </TouchableOpacity>
      )}

      {navigate && (
        <TouchableOpacity
          testID="nav-terms-conditions"
          style={
            inline ? styles.preferencesButtonInline : styles.preferencesButton
          }
          onPress={handleTermsConditionsPress}
        >
          <FileText size={16} color="#666" />
          <Text style={styles.preferencesText}>Terms & Conditions</Text>
        </TouchableOpacity>
      )}

      <View style={inline ? styles.syncSectionInline : styles.syncSection}>
        <SyncStatus
          showDetails={true}
          hideButtons={true}
          style={inline ? styles.syncStatusInline : styles.syncStatusDefault}
        />
      </View>

      {!hideLogout && (
        <TouchableOpacity
          testID="nav-logout"
          style={inline ? styles.logoutButtonInline : styles.logoutButton}
          onPress={handleLogout}
        >
          <LogOut size={16} color="#dc3545" />
          <Text style={styles.logoutText}>Logout</Text>
        </TouchableOpacity>
      )}

      <View
        testID="app-version-container"
        style={inline ? styles.versionContainerInline : styles.versionContainer}
      >
        <Text testID="app-version-text" style={styles.versionText}>
          {versionDisplay}
        </Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <TouchableOpacity
        testID="nav-account-trigger"
        ref={buttonRef}
        style={[
          inline
            ? styles.triggerInline
            : noBorder
            ? styles.triggerNoBorder
            : styles.trigger,
          disabled && styles.triggerDisabled,
        ]}
        onPress={toggleDropdown}
        disabled={disabled}
      >
        <View style={[styles.avatar, disabled && styles.avatarDisabled]}>
          <Text style={styles.avatarText}>{initials}</Text>
        </View>
        {!hideSettingsText && (
          <Text
            style={[
              styles.settingsText,
              disabled && styles.settingsTextDisabled,
            ]}
          >
            Account
          </Text>
        )}
        <ChevronDown
          size={12}
          color={disabled ? '#ccc' : '#666'}
          style={{
            transform: [{ rotate: isOpen ? '180deg' : '0deg' }],
          }}
        />
      </TouchableOpacity>

      {isWeb || inline ? (
        // Web or inline mode: Use absolute/relative positioning
        <>
          {isOpen && (
            <>
              {!inline && (
                <TouchableOpacity
                  style={styles.overlay}
                  onPress={closeDropdown}
                  activeOpacity={1}
                />
              )}
              <View style={inline ? styles.dropdownInline : styles.dropdownWeb}>
                {DropdownContent}
              </View>
            </>
          )}
        </>
      ) : (
        // Mobile: Use Modal
        <Modal
          visible={isOpen}
          transparent
          animationType="fade"
          onRequestClose={closeDropdown}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            onPress={closeDropdown}
            activeOpacity={1}
          >
            <View style={styles.dropdownMobile}>{DropdownContent}</View>
          </TouchableOpacity>
        </Modal>
      )}

      <SendFeedbackDialog
        visible={sendFeedbackOpen}
        onClose={() => setSendFeedbackOpen(false)}
      />
      <ConfirmationDialog
        visible={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleConfirmLogout}
        title="Logout"
        message={`You are currently impersonating ${
          session?.target_user?.name || 'another user'
        }. Logging out will end the impersonation session and log out your account.`}
        confirmText="Logout"
        confirmButtonStyle="danger"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    backgroundColor: '#f8f9fa',
    gap: 6,
    minHeight: 32,
  },
  triggerNoBorder: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 6,
    borderRadius: 6,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  triggerInline: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    gap: 12,
    borderRadius: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#ff6b35',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarDisabled: {
    backgroundColor: '#ccc',
  },
  avatarText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  settingsText: {
    flex: 1,
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  triggerDisabled: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
      },
    }),
  },
  settingsTextDisabled: {
    color: '#ccc',
  },
  overlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: 100,
    paddingRight: 20,
  },
  dropdown: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    minWidth: 280,
    maxWidth: 320,
  },
  dropdownWeb: {
    position: 'absolute' as any,
    top: '100%',
    right: 0,
    marginTop: 8,
    zIndex: 1000,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    minWidth: 280,
    maxWidth: 320,
  },
  dropdownMobile: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    minWidth: 280,
    maxWidth: 320,
  },
  dropdownInline: {
    backgroundColor: 'transparent',
    marginTop: 4,
  },
  dropdownContentInline: {
    backgroundColor: 'transparent',
  },
  userInfo: {
    padding: 16,
  },
  userInfoInline: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: '#666',
  },
  divider: {
    height: 1,
    backgroundColor: '#e9ecef',
    marginHorizontal: 16,
  },
  syncSection: {
    padding: 16,
  },
  syncSectionInline: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 8,
    paddingBottom: 16,
    paddingHorizontal: 16,
    gap: 12,
  },
  logoutButtonInline: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 4,
    gap: 12,
    borderRadius: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  logoutText: {
    fontSize: 14,
    color: '#dc3545',
    fontWeight: '500',
  },
  syncStatusDefault: {
    backgroundColor: 'transparent',
    padding: 0,
    borderWidth: 0,
    borderColor: 'transparent',
  },
  syncStatusInline: {
    backgroundColor: 'transparent',
    padding: 0,
    borderWidth: 0,
    borderColor: 'transparent',
  },
  preferencesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 8,
    paddingBottom: 8,
    paddingHorizontal: 16,
    gap: 12,
  },
  preferencesButtonInline: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 4,
    gap: 12,
    borderRadius: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  preferencesText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  versionContainer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    alignItems: 'center',
  },
  versionContainerInline: {
    paddingVertical: 8,
    paddingHorizontal: 4,
    alignItems: 'center',
  },
  versionText: {
    fontSize: 12,
    color: '#999',
  },
});
